import { IonItem, IonLabel } from '@ionic/react';
import React, { ReactElement } from 'react';
import {
  EventNotification,
  ListEventNotificationsDocument,
  ListEventNotificationsQuery,
  useMarkEventNotificationsReadMutation,
} from '../../api/graphql/generated';
import { trackNavigatedFromNotification } from '../../config/analytics';
import { useNavigateTo } from '../../pages/redirect.hooks';
import { formatDate } from '../../util/date-utils';
import { NotificationOptions } from './notification-options';

interface NotificationProps {
  notification: EventNotification;
}

const NotificationItem = (props: NotificationProps): ReactElement => {
  const { notification } = props;
  const [navigateTo] = useNavigateTo();

  const [markEventNotificationsReadMutation] =
    useMarkEventNotificationsReadMutation();

  const markRead = (eventId: string) => {
    markEventNotificationsReadMutation({
      variables: {
        input: {
          eventIds: [eventId],
        },
      },
      update: (cache) => {
        const cacheData = cache.readQuery<ListEventNotificationsQuery>({
          query: ListEventNotificationsDocument,
          variables: { input: { channelType: 'user' } },
        });
        const items: EventNotification[] = [
          ...(cacheData?.listEventNotifications.items ?? []),
        ] as EventNotification[];
        const clonedItems = JSON.parse(
          JSON.stringify(items)
        ) as EventNotification[];
        for (const i in clonedItems) {
          if (clonedItems[i].eventId === eventId) {
            clonedItems[i] = {
              ...clonedItems[i],
              read: true,
            };
          }
        }

        cache.writeQuery({
          query: ListEventNotificationsDocument,
          variables: { input: { channelType: 'user' } },
          data: {
            listEventNotifications: {
              ...cacheData?.listEventNotifications,
              items: clonedItems,
            },
          },
        });
      },
    });
  };

  const navigateToLink = (
    e: React.MouseEvent<HTMLIonItemElement, MouseEvent>
  ) => {
    e.preventDefault();
    markRead(notification.eventId);
    if (notification.url) {
      navigateTo({ url: notification.url });
      trackNavigatedFromNotification({ source: notification.source });
    }
  };

  return (
    <div className={notification.read ? 'read' : 'unread'}>
      <IonItem
        key={notification.id}
        lines="full"
        detail={false}
        onClick={navigateToLink}
        href={`${notification.url}`}
        className={'transparent-background'}
      >
        <IonLabel>
          <h2>
            <b>{notification.title}</b>
          </h2>
          {notification.body && <h2>{notification.body}</h2>}
          <p>{formatDate(notification.sentAt.toString())}</p>
        </IonLabel>
        {notification.read !== true && (
          <NotificationOptions
            notification={notification}
            onMarkRead={() => {
              markRead(notification.eventId);
            }}
          />
        )}
      </IonItem>
    </div>
  );
};

export { NotificationItem };
